/**
 * RACKET-LAB-ONBOARDING-1 — First-login walkthrough (Jun 2026).
 *
 * Self-contained coachmark overlay invoked from RacketLabHome (and
 * replay-able from RacketLabSettings). 100% frontend · localStorage
 * persistence · cero DB writes · cero endpoints · cero customer-facing.
 *
 * STRICT INVARIANTS:
 *   • RBAC-aware: only the 13 module-card steps the role would actually
 *     see appear in the tour. Topbar + intro/outro steps always shown.
 *   • Reversible: `Skip` and the close button mark the tour as done.
 *     Replay button in Settings clears the flag and re-mounts the tour.
 *   • i18n EN/ES via `racketLabApp.onboarding.*`. Other languages fall
 *     back to EN via the existing LangProvider chain.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLang } from "@/i18n";
import {
  Sparkles, ArrowLeft, ArrowRight, X, Play, CheckCheck, GraduationCap,
} from "lucide-react";

const STORAGE_KEY = "rl_onboarding_done_v1";

// Steps that are always shown (not gated by RBAC).
const FIXED_STEPS = [
  { key: "intro", icon: GraduationCap },
  { key: "topbar", icon: Sparkles },
];
const OUTRO_STEPS = [
  { key: "outro", icon: CheckCheck },
];

// Helpers --------------------------------------------------------------
export const isOnboardingDone = () => {
  try {
    return typeof window !== "undefined" && window.localStorage.getItem(STORAGE_KEY) === "true";
  } catch (_e) {
    return false;
  }
};

export const markOnboardingDone = () => {
  try { window.localStorage.setItem(STORAGE_KEY, "true"); } catch (_e) { /* localStorage unavailable */ }
};

export const resetOnboarding = () => {
  try { window.localStorage.removeItem(STORAGE_KEY); } catch (_e) { /* localStorage unavailable */ }
};

// Main component -------------------------------------------------------
const RacketLabOnboardingTour = ({ open, onClose, moduleSteps }) => {
  const { t } = useLang();
  const allSteps = useMemo(
    () => [...FIXED_STEPS, ...moduleSteps.map((m) => ({ key: `module:${m.key}`, icon: m.icon, isModule: true, moduleKey: m.key })), ...OUTRO_STEPS],
    [moduleSteps],
  );
  // The parent passes `key={open ? "open" : "closed"}` so this component
  // remounts on every (re-)open. That means `idx` naturally starts at 0
  // without needing a state-resetting useEffect — sidesteps the
  // react-hooks/set-state-in-effect lint rule entirely.
  const [idx, setIdx] = useState(0);

  // A11y refs: dialog box (focus-trap perimeter) + previously-focused
  // element so we can restore focus on close.
  const dialogRef = useRef(null);
  const previousActiveRef = useRef(null);

  // Stable IDs for ARIA labelling — initialised lazily via useState so
  // the value is computed once per mount without mutating refs during
  // render (which would trip the react-hooks/purity lint rule).
  const [ariaIds] = useState(() => {
    const seed = Math.random().toString(36).slice(2, 8);
    return { titleId: `rl-onb-title-${seed}`, bodyId: `rl-onb-body-${seed}` };
  });
  const { titleId, bodyId } = ariaIds;

  // Focus-trap + Esc-to-close. Effect runs once on mount (because the
  // parent forces a fresh mount on every (re-)open via the `key` prop).
  useEffect(() => {
    if (!open) return undefined;
    previousActiveRef.current = typeof document !== "undefined" ? document.activeElement : null;
    const queryFocusables = () => {
      if (!dialogRef.current) return [];
      const nodes = dialogRef.current.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      return Array.from(nodes).filter((el) => !el.hasAttribute("disabled") && !el.getAttribute("aria-hidden"));
    };
    // Initial focus: prefer the "Next" / "Finish" CTA (primary action)
    // so keyboard users land on the most likely action. Fall back to
    // the first focusable in the dialog.
    const initialFocus = () => {
      const root = dialogRef.current;
      if (!root) return;
      const primary = root.querySelector('[data-testid="rl-onboarding-next"], [data-testid="rl-onboarding-finish"]');
      const target = primary || queryFocusables()[0];
      if (target && typeof target.focus === "function") target.focus();
    };
    initialFocus();
    const handleKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        // Treat Esc as Skip — closes the tour AND marks it done (safe
        // default; consistent with `Skip` / `X` controls per spec).
        markOnboardingDone();
        if (onClose) onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const list = queryFocusables();
      if (list.length === 0) {
        e.preventDefault();
        return;
      }
      const first = list[0];
      const last = list[list.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !dialogRef.current?.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !dialogRef.current?.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", handleKey, true);
    return () => {
      document.removeEventListener("keydown", handleKey, true);
      const prev = previousActiveRef.current;
      if (prev && typeof prev.focus === "function") {
        // Restore focus to the trigger only if the element is still in
        // the document (it might've been unmounted during navigation).
        if (typeof document !== "undefined" && document.contains(prev)) {
          prev.focus();
        }
      }
    };
  }, [open, onClose]);

  if (!open) return null;
  const step = allSteps[idx];
  const isFirst = idx === 0;
  const isLast = idx === allSteps.length - 1;
  const Icon = step?.icon || Sparkles;

  const titleKey = step.isModule
    ? `racketLabApp.modules.${step.moduleKey}.title`
    : `racketLabApp.onboarding.steps.${step.key}.title`;
  const bodyKey = step.isModule
    ? `racketLabApp.onboarding.moduleBody.${step.moduleKey}`
    : `racketLabApp.onboarding.steps.${step.key}.body`;

  const moduleBodyFallback = step.isModule
    ? t(`racketLabApp.modules.${step.moduleKey}.desc`)
    : "";

  const body = step.isModule
    ? (() => {
        const resolved = t(bodyKey);
        if (resolved && resolved !== bodyKey) return resolved;
        return moduleBodyFallback;
      })()
    : t(bodyKey);

  const close = () => {
    markOnboardingDone();
    onClose && onClose();
  };

  return (
    <div
      data-testid="rl-onboarding-overlay"
      className="fixed inset-0 z-40 flex items-end justify-center bg-slate-900/30 backdrop-blur-sm sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={bodyId}
    >
      <div
        data-testid="rl-onboarding-card"
        ref={dialogRef}
        className="m-3 w-full max-w-md rounded-2xl border border-emerald-200 bg-white p-5 shadow-2xl"
      >
        <header className="mb-3 flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700">
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wide text-emerald-700" data-testid="rl-onboarding-step-counter">
                {t("racketLabApp.onboarding.stepCounter", { current: String(idx + 1), total: String(allSteps.length) })}
              </p>
              <h3 className="text-base font-semibold text-slate-900" id={titleId} data-testid="rl-onboarding-step-title">
                {t(titleKey)}
              </h3>
            </div>
          </div>
          <button
            type="button"
            onClick={close}
            data-testid="rl-onboarding-close"
            className="rounded-md p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
            aria-label={t("racketLabApp.onboarding.close")}
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div id={bodyId} data-testid="rl-onboarding-step-body" className="mb-4 text-sm leading-6 text-slate-700">
          {body}
        </div>

        <footer className="flex flex-wrap items-center justify-between gap-2">
          <button
            type="button"
            onClick={close}
            data-testid="rl-onboarding-skip"
            className="text-xs font-medium text-slate-500 underline hover:text-slate-700"
          >
            {t("racketLabApp.onboarding.skip")}
          </button>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setIdx((i) => Math.max(0, i - 1))}
              disabled={isFirst}
              data-testid="rl-onboarding-prev"
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> {t("racketLabApp.onboarding.prev")}
            </button>
            {isLast ? (
              <button
                type="button"
                onClick={close}
                data-testid="rl-onboarding-finish"
                className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700"
              >
                <CheckCheck className="h-3.5 w-3.5" /> {t("racketLabApp.onboarding.finish")}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setIdx((i) => Math.min(allSteps.length - 1, i + 1))}
                data-testid="rl-onboarding-next"
                className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700"
              >
                {t("racketLabApp.onboarding.next")} <ArrowRight className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
};

// Optional banner — renders the "Start quick tour" CTA when the tour
// has not yet been completed. Decoupled from the overlay so it can be
// mounted independently (e.g. inside Settings as "Replay tour").
export const RacketLabOnboardingBanner = ({ onStart, label }) => {
  const { t } = useLang();
  return (
    <button
      type="button"
      onClick={onStart}
      data-testid="rl-onboarding-start-button"
      className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-800 shadow-sm hover:bg-emerald-100"
    >
      <Play className="h-3.5 w-3.5" />
      {label || t("racketLabApp.onboarding.startBanner")}
    </button>
  );
};

export default RacketLabOnboardingTour;
